import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import api, { extractFieldErrorFromApiError, getApiErrorMessage } from '../../services/api';
import {
  AdminStoreCreateForm,
  AdminStoreList,
  AdminStoreDetail,
  AdminCompanyOption,
  IdentityUser,
  IdentityUserDetail
} from '../../types/index';
import { useManagementAuth } from '../../auth/ManagementAuthContext';

const StoreManagementTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // 課題111: 企業管理者として登録する操作は Re:Crew運営者(superadmin)のみ。トグルの表示可否とトークン送出に使う。
  const { managementToken, isSuperadmin } = useManagementAuth();

  // 店舗管理用状態
  const [stores, setStores] = useState<AdminStoreList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStore, setEditingStore] = useState<AdminStoreList | null>(null);
  const [formData, setFormData] = useState<AdminStoreCreateForm>({
    name: '',
    address: '',
    phone: '',
    email: '',
    liff_id: '',
    line_channel_secret: '',
    line_channel_access_token: '',
  });
  const [storeInitialSetup, setStoreInitialSetup] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<AdminCompanyOption[]>([]);
  const [companySelection, setCompanySelection] = useState<AdminCompanyOption | string | null>(null);
  const [issuedStoreId, setIssuedStoreId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState<AdminStoreList | null>(null);

  // 詳細ダイアログ用の状態
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<AdminStoreDetail | null>(null);

  // 送信試行フラグ
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // APIから返ってくるフィールド単位のエラー（例: LINE認証情報の重複）
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ユーザー管理用状態
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [currentStore, setCurrentStore] = useState<AdminStoreList | null>(null);
  const [users, setUsers] = useState<IdentityUser[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<IdentityUserDetail | null>(null);
  const [userFormData, setUserFormData] = useState({
    email: ''
  });
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<IdentityUser | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [userFieldErrors, setUserFieldErrors] = useState<Record<string, string>>({});
  const [userActionMessage, setUserActionMessage] = useState<string | null>(null);
  // 課題111: 新規ユーザーを企業管理者として登録するか（superadmin のみ操作可）
  // 権限（owner/manager/viewer）は運用上参照されないため選択UIは廃止。企業管理者作成時は API 側で 'manager' 固定。
  const [userIsCompanyAdmin, setUserIsCompanyAdmin] = useState(false);

  // 最初のエラーフィールドにスクロールする関数
  const scrollToFirstError = () => {
    setTimeout(() => {
      const errorField = document.querySelector('.Mui-error input, .Mui-error textarea');
      if (errorField) {
        errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorField as HTMLElement).focus();
      }
    }, 100);
  };

  // Store IDをクリップボードにコピーする関数
  const handleCopyStoreId = async (storeId: string) => {
    try {
      await navigator.clipboard.writeText(storeId);
    } catch (err) {
    }
  };

  // 店舗一覧取得
  const fetchStores = async () => {
    setLoading(true);
    try {
      const data = await api.adminStore.getAll();
      setStores(data);
      setError(null);
    } catch (err: any) {
      setError('店舗一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 企業選択肢一覧取得
  const fetchCompanyOptions = async () => {
    try {
      const data = await api.adminCompany.getAll();
      setCompanyOptions(data);
    } catch (err) {
      setCompanyOptions([]);
    }
  };

  // ユーザー一覧取得
  const fetchUsers = async (storeId: string) => {
    setUserLoading(true);
    try {
      const users = await api.adminIdentityUser.getStoreUsers(storeId);
      setUsers(users);
    } catch (err) {
      setUsers([]);
    } finally {
      setUserLoading(false);
    }
  };

  // ユーザー保存（Identity Platform + Custom Claim。課題122: 新規作成専用。編集はアカウント有効/無効切替のみ）
  const handleSaveUser = async () => {
    if (!currentStore) return;
    // 課題122: 編集モードでは保存アクションを持たない（パスワード直接再設定を廃止）。
    if (editingUser) return;
    const errors: Record<string, string> = {};

    if (users.length >= 3) {
      setError('ユーザーの登録は3人までです');
      return;
    }
    if (!userFormData.email.trim()) errors.userEmail = 'メールアドレスを入力してください';

    if (Object.keys(errors).length > 0) {
      setUserFieldErrors(errors);
      setTimeout(() => {
        const el = document.querySelector(`[name="${Object.keys(errors)[0]}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); (el as HTMLElement).focus(); }
      }, 100);
      return;
    }
    setUserFieldErrors({});
    setError(null);
    setUserActionMessage(null);

    setSubmitting(true);

    try {
      // 新規作成（Identity Platform + Custom Claim設定。パスワードはバックエンドで自動生成しメール送信）
      // 課題111: superadmin が企業管理者トグルをONにした場合のみ、管理JWTと共に企業管理者フラグを送る。
      const asCompanyAdmin = isSuperadmin && userIsCompanyAdmin;
      const userData = {
        email: userFormData.email,
        storeId: currentStore.id,
        ...(asCompanyAdmin
          ? { isCompanyAdmin: true, token: managementToken || undefined }
          : {}),
      };
      await api.adminIdentityUser.create(userData);
      setUserActionMessage(
        asCompanyAdmin
          ? 'ユーザーを企業管理者として作成しました。パスワード設定メールを送信しました。'
          : 'ユーザーを作成しました。パスワード設定メールを送信しました。'
      );

      setUserFormData({email: ''});
      setUserIsCompanyAdmin(false);
      setEditingUser(null);
      setError(null);
      setUserFieldErrors({});
      fetchUsers(currentStore.id);
    } catch (err: any) {
      // エラーハンドリング
      if (err.response?.status === 400) {
        const errorMessage = getApiErrorMessage(err, 'このメールアドレスは既に使用されています');
        setError(errorMessage);
      } else {
        setError('ユーザーの保存に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ユーザー削除
  const handleDeleteUser = async () => {
    if (!userToDelete || !currentStore) return;
    
    setSubmitting(true);
    
    try {
      await api.adminIdentityUser.delete(userToDelete.uid, currentStore.id);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      fetchUsers(currentStore.id);
    } catch (err: any) {
      setError('ユーザーの削除に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // ユーザーステータス切り替え（有効/無効）
  const handleToggleUserStatus = async (user: IdentityUser) => {
    if (!currentStore) return;
    
    try {
      const newDisabledStatus = !user.disabled;
      await api.adminIdentityUser.toggleStatus(user.uid, newDisabledStatus, currentStore.id);
      
      // ローカル状態を更新
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.uid === user.uid 
            ? { ...u, disabled: newDisabledStatus }
            : u
        )
      );
      
      // 編集中ユーザーの状態も更新
      if (editingUser && editingUser.uid === user.uid) {
        setEditingUser({ ...editingUser, disabled: newDisabledStatus });
      }
      
    } catch (err: any) {
      setError('ユーザーステータスの変更に失敗しました');
    }
  };

  // ユーザー編集ダイアログを開く
  const handleEditUser = async (user: IdentityUser) => {
    try {
      // 最新のユーザー詳細を取得
      const detail = await api.adminIdentityUser.getDetail(user.uid);
      setEditingUser(detail);
      setUserFormData({
        email: detail.email
      });
      setUserFieldErrors({});
      setError(null);
      setUserDialogOpen(true);
    } catch (err: any) {
      setError('ユーザー情報の取得に失敗しました');
    }
  };

  // 初期データ取得
  useEffect(() => {
    fetchStores();
    fetchCompanyOptions();
  }, []);

  // 詳細表示
  const handleViewDetails = async (store: AdminStoreList) => {
    try {
      setLoading(true);
      const storeDetail = await api.adminStore.getStoreDetail(store.id);
      setSelectedStore(storeDetail);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setError('店舗詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 編集ダイアログ開く
  const handleEditClick = async (store?: AdminStoreList) => {
    if (store) {
      try {
        // 詳細情報を取得してliff_idを含む情報を取得
        const storeDetail = await api.adminStore.getStoreDetail(store.id);
        setEditingStore(store);
        setFormData({
          name: store.name || '',
          address: store.address || '',
          phone: store.phone || '',
          email: '',
          liff_id: storeDetail.liff_id || '',
          line_channel_secret: '',
          line_channel_access_token: ''
        });
      } catch (err: any) {
        setError('店舗情報の取得に失敗しました');
        return;
      }
    } else {
      setEditingStore(null);
      setFormData({
        name: '',
        address: '',
        phone: '',
        email: '',
        liff_id: '',
        line_channel_secret: '',
        line_channel_access_token: '',
      });
    }
    setSubmitAttempted(false);
    setError(null);
    setFieldErrors({});
    setIssuedStoreId(null);
    setCompanySelection(null);
    setOpenDialog(true);
  };

  // ユーザー管理ダイアログを開く
  const handleUserManagement = (store: AdminStoreList) => {
    setCurrentStore(store);
    setUserManagementOpen(true);
    fetchUsers(store.id);
  };

  // ダイアログ閉じる
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStore(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      liff_id: '',
      line_channel_secret: '',
      line_channel_access_token: '',
    });
    setSubmitAttempted(false);
    setError(null);
    setFieldErrors({});
    setIssuedStoreId(null);
    setCompanySelection(null);
  };

  // 詳細ダイアログ閉じる
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedStore(null);
  };

  // フォーム入力変更
  const handleInputChange = (field: keyof AdminStoreCreateForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // マスク値がクリアされた場合は空文字に設定
    if (field === 'line_channel_secret' || field === 'line_channel_access_token') {
      if (value.startsWith('*')) {
        // マスク値の場合は変更しない
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // 入力中はAPIから受け取ったフィールドエラーをクリア
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // 店舗側初期設定チェックボックス変更
  const handleStoreInitialSetupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setStoreInitialSetup(checked);
    if (checked) {
      // チェック時は入力フィールドをクリア
      setFormData(prev => ({
        ...prev,
        name: '',
        address: '',
        phone: '',
        liff_id: '',
        line_channel_secret: '',
        line_channel_access_token: '',
      }));
      setIssuedStoreId(null);
    }
  };



  // 保存処理
  const handleSubmit = async () => {
    setSubmitAttempted(true);

    if (!isFormValid()) {
      scrollToFirstError();
      return;
    }

    setIsSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      // 保存前にLINE認証情報の重複チェック（編集時は自店舗除外）
      // 初期登録予約モード(storeInitialSetup)ではLINE設定は対象外なのでスキップ
      if (!storeInitialSetup) {
        const tokenForCheck = formData.line_channel_access_token.trim();
        const secretForCheck = formData.line_channel_secret.trim();
        if (tokenForCheck || secretForCheck) {
          const result = await api.adminStore.checkLineCredentials({
            line_channel_access_token: tokenForCheck || undefined,
            line_channel_secret: secretForCheck || undefined,
            exclude_store_id: editingStore?.id,
          });
          if (!result.is_available && result.conflicting_field) {
            setFieldErrors({
              [result.conflicting_field]: result.message || '入力された値は既に別の店舗に登録されています',
            });
            return;
          }
        }
      }

      // 店舗側初期設定オプションがチェックされている場合
      if (storeInitialSetup) {
        const response = await api.adminStore.reserveInitialSetup({
          name: formData.name,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          ...buildCompanySelectionPayload(),
        });
        setIssuedStoreId(response.store_id);
        setStoreInitialSetup(false);
        // フォームをリセット
        setFormData({
          name: '',
          address: '',
          phone: '',
          email: '',
          liff_id: '',
          line_channel_secret: '',
          line_channel_access_token: '',
        });
        setSubmitAttempted(false);
        setOpenDialog(false);
        // 店舗一覧を再度取得
        await fetchStores();
        return;
      }

      // バリデーション - 店舗IDの確認
      if (editingStore && !editingStore.id) {
        setError('店舗IDが取得できません');
        return;
      }

      let submitData: any = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        liff_id: formData.liff_id
      };
      
      // 編集時にLINE設定が空でない場合のみ含める
      if (editingStore) {
        if (formData.line_channel_secret.trim()) {
          submitData.line_channel_secret = formData.line_channel_secret;
        }
        if (formData.line_channel_access_token.trim()) {
          submitData.line_channel_access_token = formData.line_channel_access_token;
        }
      } else {
        // 通常の新規作成時は必須なので含める
        submitData.line_channel_secret = formData.line_channel_secret;
        submitData.line_channel_access_token = formData.line_channel_access_token;
        Object.assign(submitData, buildCompanySelectionPayload());
      }

      if (editingStore) {
        // 編集処理
        await api.adminStore.updateStore(editingStore.id, submitData);
      } else {
        // 新規作成処理
        await api.adminStore.createStore(submitData);
      }
      
      // 成功時の処理
      handleCloseDialog();
      await fetchStores();
    } catch (err: any) {
      // バックエンドから特定フィールドに紐付くエラー（LINE認証情報の重複など）が返ってきた場合は
      // 該当フィールドの helperText に表示し、入力枠を赤くする
      const fieldError = extractFieldErrorFromApiError(err);
      if (fieldError) {
        setFieldErrors(prev => ({ ...prev, [fieldError.field]: fieldError.message }));
      } else {
        setError(getApiErrorMessage(err, '保存に失敗しました'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 削除処理
  const handleDeleteStore = async () => {
    if (!storeToDelete) return;
    
    setSubmitting(true);
    
    try {
      await api.adminStore.deleteStore(storeToDelete.id);
      setDeleteDialogOpen(false);
      setStoreToDelete(null);
      await fetchStores(); // 一覧を再取得
    } catch (err: any) {
      setError('店舗の削除に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // 企業選択済みかどうか（既存企業選択 or 新規企業名入力）
  const hasCompanySelection = () => {
    if (!companySelection) return false;
    return typeof companySelection === 'string' ? companySelection.trim().length > 0 : true;
  };

  // 選択済みの企業をAPI送信用のペイロードに変換する
  const buildCompanySelectionPayload = (): { company_id?: string; new_company_name?: string } => {
    if (companySelection && typeof companySelection === 'object') {
      return { company_id: companySelection.id };
    }
    if (typeof companySelection === 'string' && companySelection.trim()) {
      return { new_company_name: companySelection.trim() };
    }
    return {};
  };

  // バリデーション
  const isFormValid = () => {
    // 店舗側初期設定オプションがチェックされている場合は店舗名・住所・電話番号・メール・企業のみ必須
    if (storeInitialSetup) {
      return !!(formData.name.trim() && formData.address.trim() && formData.phone.trim() && formData.email.trim() && hasCompanySelection());
    }

    const baseValid = formData.name.trim() &&
                    formData.address.trim() &&
                    formData.phone.trim() &&
                    formData.liff_id.trim();

    if (editingStore) {
      // 編集時はLINE設定・企業選択は対象外
      return baseValid;
    } else {
      // 新規作成時はLINE設定・企業選択も必須
      return baseValid &&
            formData.line_channel_secret.trim() &&
            formData.line_channel_access_token.trim() &&
            hasCompanySelection();
    }
  };

  // モバイル用カード表示
  const renderMobileCard = (store: AdminStoreList) => (
    <Card key={store.id} sx={{ mb: 2 }}>
      <CardContent>
        {/* 既存のコンテンツ */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            {store.name}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 1.5 }} />
        
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            住所
          </Typography>
          <Typography variant="body2">
            {store.address}
          </Typography>
        </Box>
        
        {store.phone && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              電話番号
            </Typography>
            <Typography variant="body2">
              {store.phone}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            企業
          </Typography>
          <Typography variant="body2">
            {store.company_name || '-'}
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDetails(store)}
          >
            詳細
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEditClick(store)}
          >
            編集
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SupervisorAccountIcon />}
            onClick={() => handleUserManagement(store)}
            color="secondary"
          >
            ユーザ管理
          </Button>
          {/* 削除ボタンを追加 */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setStoreToDelete(store);
              setDeleteDialogOpen(true);
            }}
            color="error"
          >
            削除
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h5" gutterBottom>
            店舗管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            店舗の追加・編集を行います。
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleEditClick()}
          sx={{ minWidth: 140 }}
        >
          新規店舗作成
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isMobile ? (
            // モバイル表示: カード
            stores.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  登録されている店舗がありません
                </Typography>
              </Box>
            ) : (
              stores.map(store => renderMobileCard(store))
            )
          ) : (
            // PC表示: テーブル
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>店舗名</TableCell>
                      <TableCell>住所</TableCell>
                      <TableCell>電話番号</TableCell>
                      <TableCell>企業</TableCell>
                      <TableCell align="center">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stores.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">
                            登録されている店舗がありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      stores.map((store) => (
                        <TableRow key={store.id} hover>
                          <TableCell>{store.name}</TableCell>
                          <TableCell>{store.address}</TableCell>
                          <TableCell>{store.phone || '-'}</TableCell>
                          <TableCell>{store.company_name || '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(store)}
                              color="info"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(store)}
                              color="primary"
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleUserManagement(store)}
                              color="secondary"
                            >
                              <SupervisorAccountIcon />
                            </IconButton>
                            {/* 削除ボタンを追加 */}
                            <IconButton
                              size="small"
                              onClick={() => {
                                setStoreToDelete(store);
                                setDeleteDialogOpen(true);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* 詳細表示ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          店舗詳細
          <IconButton
            aria-label="close"
            onClick={handleCloseDetailDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedStore && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  基本情報
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    店舗名
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.name}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    住所
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.address}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    電話番号
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.phone}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    企業
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.company_name || '-'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    LIFF ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.liff_id}
                  </Typography>
                </Box>

                {/* Store ID を追加 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Store ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedStore.id}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyStoreId(selectedStore.id)}
                      title="Store IDをコピー"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* LINEチャネルシークレット */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    チャネルシークレット
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.has_line_channel_secret ? '設定済み' : '未設定'}
                  </Typography>
                </Box>

                {/* LINEチャネルアクセストークン */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    アクセストークン
                  </Typography>
                  <Typography variant="body1">
                    {selectedStore.has_line_channel_access_token ? '設定済み' : '未設定'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    作成日時
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedStore.created_at).toLocaleString('ja-JP')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    最終更新日時
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedStore.updated_at).toLocaleString('ja-JP')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 新規作成・編集モーダル */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          {editingStore ? '店舗情報編集' : '新規店舗作成'}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* 発行済みStore IDを表示 */}
            {issuedStoreId && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      店舗IDを発行しました
                    </Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', overflowWrap: 'break-word' }}>
                      {issuedStoreId}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(issuedStoreId);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Alert>
            )}

            {/* 店舗側初期設定オプション */}
            {!editingStore && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={storeInitialSetup}
                      onChange={handleStoreInitialSetupChange}
                    />
                  }
                  label="店舗側に初期設定させる"
                  sx={{ mb: 2 }}
                />
                {storeInitialSetup && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    店舗情報を入力してください。利用登録完了メールが送信されます。
                  </Alert>
                )}
              </>
            )}

            <TextField
              fullWidth
              label="店舗名"
              value={formData.name}
              onChange={handleInputChange('name')}
              margin="normal"
              required
              error={submitAttempted && !formData.name.trim()}
              helperText={submitAttempted && !formData.name.trim() ? '店舗名は必須です' : ''}
            />
            {!editingStore && (
              <Autocomplete
                freeSolo
                fullWidth
                options={companyOptions}
                value={companySelection}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                isOptionEqualToValue={(option, value) =>
                  typeof option !== 'string' && typeof value !== 'string' && option.id === value.id
                }
                onChange={(_, newValue) => setCompanySelection(newValue)}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') setCompanySelection(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="企業"
                    margin="normal"
                    required
                    error={submitAttempted && !hasCompanySelection()}
                    helperText={
                      submitAttempted && !hasCompanySelection()
                        ? '企業を選択するか、新規企業名を入力してください'
                        : 'リストにない場合は企業名を入力すると新規登録されます'
                    }
                  />
                )}
              />
            )}
            <TextField
              fullWidth
              label="住所"
              value={formData.address}
              onChange={handleInputChange('address')}
              margin="normal"
              required
              error={submitAttempted && !formData.address.trim()}
              helperText={submitAttempted && !formData.address.trim() ? '住所は必須です' : ''}
            />
            <TextField
              fullWidth
              label="電話番号"
              value={formData.phone}
              onChange={handleInputChange('phone')}
              margin="normal"
              required
              error={submitAttempted && !formData.phone.trim()}
              helperText={submitAttempted && !formData.phone.trim() ? '電話番号は必須です' : ''}
              placeholder="例: 03-1234-5678"
            />
            {/* 店舗側初期設定時のみメールアドレス入力 */}
            {!editingStore && storeInitialSetup && (
              <TextField
                fullWidth
                label="メールアドレス"
                value={formData.email}
                onChange={handleInputChange('email')}
                margin="normal"
                required
                type="email"
                error={submitAttempted && !formData.email.trim()}
                helperText={submitAttempted && !formData.email.trim() ? 'メールアドレスは必須です' : '利用登録完了メールの送信先'}
                placeholder="例: store@example.com"
              />
            )}
            <TextField
              fullWidth
              label="LIFF ID"
              value={formData.liff_id}
              onChange={handleInputChange('liff_id')}
              margin="normal"
              required={!storeInitialSetup}
              disabled={!editingStore && storeInitialSetup}
              error={submitAttempted && !storeInitialSetup && !formData.liff_id.trim()}
              helperText={submitAttempted && !storeInitialSetup && !formData.liff_id.trim() ? 'LIFF IDは必須です' : ''}
              placeholder="例: xxxxxxxx"
            />
            <TextField
              fullWidth
              label="LINEチャネルシークレット"
              value={formData.line_channel_secret}
              onChange={handleInputChange('line_channel_secret')}
              margin="normal"
              required={!editingStore && !storeInitialSetup}
              disabled={!editingStore && storeInitialSetup}
              type="password"
              autoComplete="new-password"
              error={!!fieldErrors.line_channel_secret || (submitAttempted && !editingStore && !storeInitialSetup && !formData.line_channel_secret.trim())}
              helperText={
                fieldErrors.line_channel_secret
                  ? fieldErrors.line_channel_secret
                  : submitAttempted && !editingStore && !storeInitialSetup && !formData.line_channel_secret.trim()
                    ? 'LINEチャネルシークレットは必須です'
                    : editingStore
                      ? '変更する場合のみ入力してください'
                      : ''
              }
            />
            <TextField
              fullWidth
              label="LINEチャネルアクセストークン"
              value={formData.line_channel_access_token}
              onChange={handleInputChange('line_channel_access_token')}
              margin="normal"
              required={!editingStore && !storeInitialSetup}
              disabled={!editingStore && storeInitialSetup}
              type="password"
              autoComplete="new-password"
              error={!!fieldErrors.line_channel_access_token || (submitAttempted && !editingStore && !storeInitialSetup && !formData.line_channel_access_token.trim())}
              helperText={
                fieldErrors.line_channel_access_token
                  ? fieldErrors.line_channel_access_token
                  : submitAttempted && !editingStore && !storeInitialSetup && !formData.line_channel_access_token.trim()
                    ? 'LINEチャネルアクセストークンは必須です'
                    : editingStore
                      ? '変更する場合のみ入力してください'
                      : ''
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            color="inherit"
          >
            キャンセル
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid() || isSaving}
          >
            {isSaving ? '保存中...' : (editingStore ? '更新' : '作成')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 店舗削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          店舗削除確認
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            以下の店舗を削除してもよろしいですか？
          </DialogContentText>
          {storeToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">店舗名</Typography>
              <Typography variant="body1">{storeToDelete.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>住所</Typography>
              <Typography variant="body1">{storeToDelete.address}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            この操作は元に戻せません。削除された店舗は復元できません。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteStore} 
            color="error" 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? '削除中...' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー管理ダイアログ */}
      <Dialog open={userManagementOpen} onClose={() => setUserManagementOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          ユーザー管理 - {currentStore?.name}
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />} 
            onClick={() => {
              setEditingUser(null);
              setUserFormData({email: ''});
              setUserIsCompanyAdmin(false);
              setUserFieldErrors({});
              setError(null);
              setUserActionMessage(null);
              setUserDialogOpen(true);
            }}
            sx={{ ml: 2 }}
            disabled={users.length >= 3}
            title={users.length >= 3 ? 'ユーザー登録は3人までです' : ''}
          >
            新規作成
          </Button>
          <IconButton onClick={() => setUserManagementOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {userLoading ? <CircularProgress /> : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography color="text.secondary">
                          登録されているユーザーがありません
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.uid}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {user.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip 
                              label={user.disabled ? '無効' : '有効'} 
                              color={user.disabled ? 'error' : 'success'}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEditUser(user)}
                              title="編集"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => { 
                                setUserToDelete(user); 
                                setDeleteUserDialogOpen(true); 
                              }}
                              title="削除"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* ユーザー作成・編集ダイアログ */}
      <Dialog open={userDialogOpen} onClose={() => { setUserDialogOpen(false); setUserFieldErrors({}); setError(null); setUserActionMessage(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'ユーザー編集' : 'ユーザー新規作成'}
          <IconButton
            onClick={() => { setUserDialogOpen(false); setUserFieldErrors({}); setError(null); setUserActionMessage(null); }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* エラー表示 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 成功メッセージ表示 */}
          {userActionMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {userActionMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="メールアドレス"
            name="userEmail"
            value={userFormData.email}
            onChange={(e) => { setUserFormData({...userFormData, email: e.target.value}); setUserFieldErrors(prev => { const { userEmail, ...rest } = prev; return rest; }); }}
            margin="normal"
            type="email"
            autoComplete="off"
            required={!editingUser}
            disabled={!!editingUser} // 編集時は無効化
            error={!!userFieldErrors.userEmail}
            helperText={
              userFieldErrors.userEmail
                ? userFieldErrors.userEmail
                : editingUser
                  ? 'メールアドレスは変更できません'
                  : ''
            }
          />
          {/* 課題111: 新規ユーザーを企業管理者として登録するオプション（Re:Crew運営者のみ表示） */}
          {!editingUser && isSuperadmin && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userIsCompanyAdmin}
                    onChange={(e) => setUserIsCompanyAdmin(e.target.checked)}
                  />
                }
                label="このユーザーを企業管理者として登録する"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                ONにすると、店舗ログインに加えて企業（この店舗が属する企業）の管理画面にアクセスできる管理アカウントを作成します。
              </Typography>
            </Box>
          )}

          {/* 編集時の追加オプション */}
          {editingUser && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                アカウント管理
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => handleToggleUserStatus(editingUser)}
                sx={{ mr: 1 }}
              >
                {editingUser.disabled ? 'アカウント有効化' : 'アカウント無効化'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUserDialogOpen(false); setUserFieldErrors({}); setError(null); }}>
            {editingUser ? '閉じる' : 'キャンセル'}
          </Button>
          {/* 課題122: 新規作成時のみ保存ボタンを表示。編集はアカウント有効/無効切替（上記ボタン）のみ */}
          {!editingUser && (
            <Button
              onClick={handleSaveUser}
              variant="contained"
              disabled={submitting}
            >
              {submitting ? '保存中...' : 'ユーザー作成'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ユーザー削除確認ダイアログ */}
      <Dialog open={deleteUserDialogOpen} onClose={() => setDeleteUserDialogOpen(false)}>
        <DialogTitle>
          ユーザー削除確認
          <IconButton
            onClick={() => setDeleteUserDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            以下のユーザーを削除してもよろしいですか？
          </DialogContentText>
          {userToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">メールアドレス</Typography>
              <Typography variant="body1">{userToDelete.email}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            この操作は元に戻せません。削除されたユーザーは復元できません。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? '削除中...' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreManagementTab;